/**
 * Zero-dependency static constants and pure utilities.
 *
 * Kept in their own file specifically to avoid circular-import TDZ crashes.
 * Do NOT import from firebase.ts, constants.ts, or any module that
 * transitively imports either of those.
 */

/**
 * Converts a local datetime string (YYYY-MM-DDTHH:mm) and an IANA timezone
 * into the correct UTC ISO string. This ensures event times are always
 * stored relative to the selected timezone, not the browser's local zone.
 *
 * Example:
 *   localToUTC("2026-07-15T10:00", "America/New_York")
 *   → "2026-07-15T14:00:00.000Z"   (EDT, UTC−4)
 */
export function localToUTC(dateTimeLocal: string, timezone: string): string {
  if (!dateTimeLocal || typeof dateTimeLocal !== 'string' || !dateTimeLocal.includes('T')) {
    if (dateTimeLocal) {
      try {
        const parsed = new Date(dateTimeLocal);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString();
        }
      } catch {}
    }
    return new Date().toISOString();
  }

  const [datePart, timePart] = dateTimeLocal.split('T');
  if (!datePart || !timePart) return new Date().toISOString();

  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, min] = timePart.split(':').map(Number);

  if (isNaN(y) || isNaN(m) || isNaN(d) || isNaN(hh) || isNaN(min)) {
    return new Date().toISOString();
  }

  // Treat the input as if it were UTC — we'll correct for the offset below
  const naiveUtc = new Date(Date.UTC(y, m - 1, d, hh, min));

  // Ask Intl what offset the target timezone uses at that moment
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  }).formatToParts(naiveUtc);

  const offsetStr = parts.find(p => p.type === 'timeZoneName')?.value || '';
  const match = offsetStr.match(/GMT([+-]\d{2}):(\d{2})/);

  if (match) {
    const sign = match[1][0] === '-' ? -1 : 1;
    const hours = parseInt(match[1].slice(1), 10);
    const minutes = parseInt(match[2], 10);
    const totalOffsetMs =
      (hours * 60 + minutes) * 60 * 1000 * sign;

    // UTC = local time − offset
    return new Date(naiveUtc.getTime() - totalOffsetMs).toISOString();
  }

  // Fallback: treat input as browser-local (legacy behaviour)
  return new Date(dateTimeLocal).toISOString();
}

/**
 * Converts a UTC ISO string (e.g. "2027-06-15T14:00:00.000Z") and an IANA timezone
 * (e.g. "America/New_York") into a local datetime input string (YYYY-MM-DDTHH:mm).
 *
 * Example:
 *   utcToLocalISO("2027-06-15T14:00:00.000Z", "America/New_York")
 *   → "2027-06-15T10:00"   (EDT, UTC−4)
 */
export function utcToLocalISO(utcIsoString: string, timezone: string): string {
  if (!utcIsoString) return '';
  try {
    const d = new Date(utcIsoString);
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(d);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '00';
    return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
  } catch {
    return '';
  }
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PHONE_REGEX = /^\+?[1-9]\d{6,14}$/;

export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California',
  'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

export const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Jamaica',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Athens',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Nairobi',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Kathmandu',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
];
